import { includes, reduce } from 'libraries/lodash'
import {
  USER_CLARILIS,
  USER_CLARILIS_SHARES,
  USER_DOC_DRAFTER,
  USER_DOCUMENTS
} from 'constants/user-roles'
import {
  SET_USER_DETAILS,
  SET_USER_IS_LOGGED,
  SET_USER_ROLES,
  SET_USER_IS_CLARILIS,
  SET_USER_IS_DOCDRAFTER,
  SET_ACCESS_ROLES,
  SET_USER_IS_DOCDRAFTER_CLARILIS,
  SET_USER_HAS_SMART_FEATURES,
  SET_PRIMARY_AOL,
  SET_PRACTISING_AOL,
  SET_PRIMARY_AOL_SET_BY_USER
} from './types'
import { AppDispatch, RootState } from 'redux/store'
import { setSmartSearchEnabled } from './search'

export const setUserDetails = (details: { email: string, isExternalAuth: boolean, primaryAolSetByUser?: boolean }): { type: typeof SET_USER_DETAILS, details: { email: string, isExternalAuth: boolean, primaryAolSetByUser?: boolean } } => ({
  type: SET_USER_DETAILS,
  details
})

export const setUserIsLogged = (isLogged: boolean): { type: typeof SET_USER_IS_LOGGED, isLogged: boolean } => ({
  type: SET_USER_IS_LOGGED,
  isLogged
})

export const isRoles = (state: RootState, ...props: string[]): boolean => {
  return props.length !== 0
    ? reduce(
      props,
      (result, role) => result && includes(state.user.roles, role),
      true
    )
    : true
}

export const isOneOfRoles = (state: RootState, ...roles: string[]): boolean => {
  return roles.length !== 0
    ? reduce(
      roles,
      (result, role) => result || includes(state.user.roles, role),
      false
    )
    : false
}

export const setAccessRoles = (accessRoles: string[]) => {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch({
      type: SET_ACCESS_ROLES,
      accessRoles
    })

    const state = getState()

    const isClarilis = hasAccessLevel(state, USER_CLARILIS, USER_CLARILIS_SHARES)
    dispatch({
      type: SET_USER_IS_CLARILIS,
      isClarilis
    })

    const isDocDrafter = hasAccessLevel(state, USER_DOC_DRAFTER)
    dispatch({
      type: SET_USER_IS_DOCDRAFTER,
      isDocDrafter
    })

    const isDocDrafterClarilis = hasAccessRolesWith(state, USER_DOCUMENTS, USER_CLARILIS)
    dispatch({
      type: SET_USER_IS_DOCDRAFTER_CLARILIS,
      isDocDrafterClarilis
    })
  }
}

export const hasAccessLevel = (state: RootState, ...accessLevels: string[]): boolean => {
  const { accessRoles } = state.user
  if (accessRoles.length === 0) return false

  return accessRoles.some(accessRole =>
    accessLevels.some(level => accessRole.endsWith(level))
  )
}

export const hasContentType = (accessRoles: string[], ...contentTypes: string[]): boolean => {
  if (accessRoles.length === 0) return false

  return accessRoles.some(userRole =>
    contentTypes.some(contentType => userRole.includes(contentType))
  )
}

export const hasAccessRolesWith = (state: RootState, contentType: string, accessLevel: string): boolean => {
  const { accessRoles } = state.user
  if (accessRoles.length === 0) return false

  return accessRoles.some(accessRole => {
    const roleParts = accessRole.split('_')

    if (roleParts.length >= 3) {
      return roleParts[1].includes(contentType) && roleParts[2].includes(accessLevel)
    }
    return false
  })
}

export const setAccessToSmartFeatures = (hasSmartFeatures: boolean): { type: typeof SET_USER_HAS_SMART_FEATURES, hasSmartFeatures: boolean } => {
  if (!hasSmartFeatures) {
    setSmartSearchEnabled(false)
  }

  return {
    type: SET_USER_HAS_SMART_FEATURES,
    hasSmartFeatures
  }
}

export const setUserRoles = (roles: string[]): { type: typeof SET_USER_ROLES, roles: string[] } => ({
  type: SET_USER_ROLES,
  roles
})

export const setPrimaryAol = (primaryAol: string): { type: typeof SET_PRIMARY_AOL, primaryAol: string } => ({
  type: SET_PRIMARY_AOL,
  primaryAol
})

export const setPractisingAol = (practisingAol: string): { type: typeof SET_PRACTISING_AOL, practisingAol: string } => ({
  type: SET_PRACTISING_AOL,
  practisingAol
})

export const setPrimaryAolSetByUser = (primaryAolSetByUser: boolean): { type: typeof SET_PRIMARY_AOL_SET_BY_USER, primaryAolSetByUser: boolean } => ({
  type: SET_PRIMARY_AOL_SET_BY_USER,
  primaryAolSetByUser
})
